import { Link } from "react-router-dom";
import { Loader2, Check, X, CalendarRange, Users } from "lucide-react";
import { fmtClassDay, fmtDateOnly } from "@/lib/datetime";

/**
 * Derived-attendance grid. Students = rows, class days (Mon/Thu) = columns,
 * newest → oldest left-to-right. First column (student name) is sticky.
 *
 * props:
 *  - data: { semester|null, class_days: [..desc], students: [{id, full_name, present_dates}] }
 *  - loading: bool
 *  - linkBase: string|null — if set, student names link to `${linkBase}${id}`
 *  - emptyText: string — shown when there are no students
 */
export default function AttendanceTable({ data, loading, linkBase = null, emptyText = "No students found." }) {
  if (loading) {
    return (
      <div className="mt-6 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!data) return null;

  if (!data.semester) {
    return (
      <div className="mt-6 bg-card border border-border rounded-lg p-10 text-center text-muted-foreground" data-testid="attendance-no-semester">
        <CalendarRange className="h-8 w-8 mx-auto mb-3 opacity-40" />
        No active semester found. An admin can create one under <span className="font-medium text-foreground">Semesters</span>.
      </div>
    );
  }

  const { semester, class_days, students } = data;

  return (
    <div data-testid="attendance-grid">
      {/* Semester banner */}
      <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="attendance-semester-banner">
        <CalendarRange className="h-4 w-4" />
        <span className="font-medium text-foreground">{semester.name}</span>
        <span>· {fmtDateOnly(semester.start_date)} – {fmtDateOnly(semester.end_date)}</span>
      </div>

      {students.length === 0 ? (
        <div className="mt-4 bg-card border border-border rounded-lg p-12 text-center text-muted-foreground" data-testid="attendance-empty">
          <Users className="h-8 w-8 mx-auto mb-3 opacity-40" />
          {emptyText}
        </div>
      ) : class_days.length === 0 ? (
        <div className="mt-4 bg-card border border-border rounded-lg p-12 text-center text-muted-foreground" data-testid="attendance-no-classdays">
          No class days have occurred yet in this semester.
        </div>
      ) : (
        <div className="mt-4 bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="border-collapse text-sm" data-testid="attendance-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="sticky left-0 z-20 bg-card text-left font-semibold px-4 py-3 border-r border-border min-w-[160px]">
                    Student
                  </th>
                  {class_days.map((d) => (
                    <th key={d} className="px-3 py-3 font-medium text-muted-foreground whitespace-nowrap text-center" data-testid={`attendance-col-${d}`}>
                      {fmtClassDay(d)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map((s) => {
                  const present = new Set(s.present_dates);
                  return (
                    <tr key={s.id} className="border-b border-border last:border-0" data-testid={`attendance-row-${s.id}`}>
                      <td className="sticky left-0 z-10 bg-card font-medium px-4 py-3 border-r border-border whitespace-nowrap">
                        {linkBase ? (
                          <Link
                            to={`${linkBase}${s.id}`}
                            className="text-primary hover:underline"
                            data-testid={`attendance-student-${s.id}`}
                          >
                            {s.full_name}
                          </Link>
                        ) : (
                          <span data-testid={`attendance-student-${s.id}`}>{s.full_name}</span>
                        )}
                      </td>
                      {class_days.map((d) => {
                        const isPresent = present.has(d);
                        return (
                          <td
                            key={d}
                            className="px-3 py-3 text-center"
                            data-testid={`attendance-cell-${s.id}-${d}`}
                            title={`${fmtClassDay(d)} — ${isPresent ? "Present" : "Absent"}`}
                          >
                            {isPresent ? (
                              <Check className="h-4 w-4 mx-auto text-[hsl(var(--success))]" aria-label="Present" />
                            ) : (
                              <X className="h-4 w-4 mx-auto text-destructive/60" aria-label="Absent" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        Class days are Monday &amp; Thursday. A record on a class day counts as present.
      </p>
    </div>
  );
}
